import type { RouterConfig } from "@nuxt/schema";

const queryWithoutPage = (q: Record<string, unknown>) => {
	const { p: _p, ...rest } = q;
	return rest;
};

const sameQueryIgnoringPage = (
	a: Record<string, unknown>,
	b: Record<string, unknown>
) => {
	const ax = queryWithoutPage(a);
	const bx = queryWithoutPage(b);
	const aKeys = Object.keys(ax).sort();
	const bKeys = Object.keys(bx).sort();
	if (aKeys.length !== bKeys.length) return false;
	return aKeys.every(
		(k, i) => k === bKeys[i] && String(ax[k]) === String(bx[bKeys[i]])
	);
};

export default <RouterConfig>{
	scrollBehavior(to, from, savedPosition) {
		// Same path, only `?p` differs → don't scroll (Load More click).
		if (
			from &&
			to.path === from.path &&
			sameQueryIgnoringPage(
				to.query as Record<string, unknown>,
				from.query as Record<string, unknown>
			) &&
			to.query.p !== from.query.p
		) {
			return false;
		}

		// Initial load / reload: `from` is START_LOCATION, so it carries no name.
		// Don't force a position here. Pages resolve their data asynchronously, so
		// `page:finish` can fire a second or more after first paint — by then the
		// reader may have scrolled, and forcing top yanks them back to the start.
		// A restored position still wins, and a hash still scrolls (deep links).
		if (!from?.name) {
			if (savedPosition) return savedPosition;
			if (!to.hash) return false;
		}

		// For real navigations (e.g. category → PDP) wait until the destination
		// page has actually mounted before applying scroll. Otherwise the OLD
		// page visibly scrolls to top during the in-flight transition.
		return new Promise((resolve) => {
			const nuxtApp = useNuxtApp();
			nuxtApp.hooks.hookOnce("page:finish", () => {
				nextTick(() => {
					if (savedPosition) {
						resolve(savedPosition);
					} else if (to.hash) {
						resolve({ el: to.hash, behavior: "smooth" });
					} else {
						resolve({ left: 0, top: 0 });
					}
				});
			});
		});
	}
};
